import { Component, Input, NgZone, OnDestroy, OnInit } from "@angular/core";
import {
  AnalyticsFilter,
  AnalyticsService,
  AuthorizationService,
  ExportService,
  LoggingService,
  ManualsOpenedData,
  TimePeriod,
  ToastrService,
  UserLineChartDataPoint,
  UserSessionsChartData,
  UserSessionsData,
} from "app/shared";
import {
  ChartData,
  ChartOptions,
  ChartType,
  ChartTypeRegistry,
  Plugin,
} from "chart.js";
//TODO: this StringFormatterService may be available to import from app/shared
import { StringFormatterService } from "app/shared/service/string-formatter.service";
import { BaseChartLogic } from "app/analytics/BaseChartLogic";
import {
  ChartDefinition,
  ChartDefinitions,
} from "app/analytics/chartDefinitions.config";
import {
  ManualsDownloadedConfig,
  UserAnalyticConfiguration,
  UserSessionsConfig,
} from "app/shared/model/charts/user-analytic-configurations.model";

@Component({
  selector: "users-sessions-chart",
  templateUrl: "../../common-chart-template.component.html",
  standalone: false,
})
export class UsersSessionsChartComponent
  extends BaseChartLogic<UserSessionsConfig, UserSessionsData>
  implements OnInit, OnDestroy
{
  //I am here, change type for DownloadedManualsConfig

  @Input() startDate: Date | null = null;
  @Input() endDate: Date | null = null;
  @Input() showTitle: boolean = true;

  public userSessionData: ChartData<"line"> = {
    labels: ["January", "February", "March", "April", "May", "June", "July"],
    datasets: [
      {
        data: [22, 59, 80, 81, 105, 55, 117],
        label: "User Sessions",
        borderColor: "#3cba9f",
      },
    ],
  };
  public userSessionChartOptions: ChartOptions = {
    responsive: true,
  };
  public userSessionChartType: ChartType = "line";

  //TODO: what is StringFormatterService, why is it not imported?
  constructor(
    _analyticsService: AnalyticsService,
    _authService: AuthorizationService,
    _toastrService: ToastrService,
    _exportService: ExportService,
    _loggingService: LoggingService,
    _ngZone: NgZone,
    private sf: StringFormatterService
  ) {
    super(
      _analyticsService,
      _authService,
      _exportService,
      _toastrService,
      _loggingService,
      UserSessionsConfig,
      _ngZone
    );

    this.handleDataSubscription(this.analyticsService.userSessionsDataChange);

    this.analyticsService.userSessionsDataChange.subscribe((data) => {
      this.setData(data);
    });

    this.analyticsService.userAnalyticsConfigurationChartDisplayOptionsChange.subscribe(
      () => {
        this.chartData.labels = this.userSessionData.labels;
        this.chartData.datasets[0].data = this.userSessionData.datasets[0].data;
        if(this.analyticsData?.labels) {
          this.analyticsData.labels = this.userSessionData.labels;
        }
        this.analyticsData.data = this.userSessionData.datasets[0].data;
        this.config.generalSettings.labels = this.userSessionData.labels;
      }
    );
  }

  dataPointKey: string = "Total"; //I am here
  protected getDataPointDisplayValue(dataPoint: UserLineChartDataPoint): any {
    return dataPoint.Total;
  }

  buildFilterSettings(): void {
    this.loggingService.log("UserSessionsChartComponent - buildFilterSettings");
    this.filterSettings.FilterByAllEmployees = "true";
    this.addPeriodtoFilterSettings();
    this.addPermissionsToFilterSettings();
    this.addExpirationPeriodtoFilterSettings();
  }

  protected processDataForChart(data?: UserSessionsData) {
    // Call the parent method if needed
    this.setData(data);

    super.processDataForChart(data);

    this.loggingService.log(
      "Total Users Chart - processDataForChart - custom logic applied"
    );
  }

  refreshData(): void {
    this.isLoading = true;
    this.filterSettings.FilterByAllEmployees = "true";
    this.addPeriodtoFilterSettings();
    this.addPermissionsToFilterSettings();
    this.addExpirationPeriodtoFilterSettings();
    this.loggingService.log("Getting User Sessions Data");
    this.analyticsService.getUserSessionsData(this.filterSettings);
  }

  ngOnInit(): void {
    super.init();
  }

  ngOnDestroy(): void {
    super.cleanup();
  }

  private setData(data: UserSessionsData): void {
    if (data && data.DataPoints.length > 0) {
      this.isLoading = false;
      this.loggingService.log("User Sessions Data Changed");
      this.loggingService.log(data);
      //aggregate data points on the daily level
      let dataPointsList = data.DataPoints.sort((a, b) => {
        let aDate = new Date(a.RangeStart);
        let bDate = new Date(b.RangeStart);
        return aDate.getTime() - bDate.getTime();
      });

      const aggregatedData: { date: string; total: number }[] = [];
      let currentDate = "";
      let currentTotal = 0;

      for (const dataPoint of dataPointsList) {
        let date = new Date(dataPoint.RangeStart);
        const formattedDate = this.sf.formatDateTime(date);
        if (formattedDate !== currentDate) {
          if (currentDate) {
            // Add the previous date's points to the aggregated data
            aggregatedData.push({ date: currentDate, total: currentTotal });
          }
          // Reset for the new date
          currentDate = formattedDate;
          currentTotal = dataPoint.Total;
        } else {
          // Accumulate points for the same date
          currentTotal += dataPoint.Total;
        }
      }

      // Add the last date's points to the aggregated data
      if (currentDate) {
        aggregatedData.push({ date: currentDate, total: currentTotal });
      }
      this.userSessionData.labels = aggregatedData.map((item) => item.date);
      this.userSessionData.datasets[0].data = aggregatedData.map(
        (item) => item.total
      );
      this.chartData.labels = aggregatedData.map((item) => item.date);
      this.chartData.datasets[0].data = aggregatedData.map(
        (item) => item.total
      );
      this.config.generalSettings.labels = aggregatedData.map(
        (item) => item.date
      );
    }
    this.hasData = true;
  }
}
